import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Users, BookOpen, LayoutGrid } from "lucide-react";
import { SERVICES, getServiceBySlug } from "../data/services";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  const { prev, next } = useMemo(() => {
    if (!service) return { prev: null, next: null };
    const idx = SERVICES.findIndex((s) => s.slug === slug);
    return {
      prev: idx > 0 ? SERVICES[idx - 1] : SERVICES[SERVICES.length - 1],
      next: idx < SERVICES.length - 1 ? SERVICES[idx + 1] : SERVICES[0],
    };
  }, [slug, service]);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  return (
    <main data-testid={`service-detail-${service.slug}`}>
      {/* HERO */}
      <section className="bg-white border-b border-[#E5E7EB]" data-testid="service-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] nnc-text hover:nnc-gold-text transition-colors"
            data-testid="service-back-link"
          >
            <ArrowLeft className="h-3 w-3" /> Back to Directory
          </Link>

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start nnc-fade-up">
            <div className="lg:col-span-2">
              <div className="border border-[#00205B] aspect-square flex items-center justify-center nnc-ceramic-soft-bg">
                <span className="text-5xl font-bold nnc-text tracking-tight">
                  {service.number}
                </span>
              </div>
            </div>
            <div className="lg:col-span-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="nnc-gold-rule" />
                <span className="nnc-overline">{service.category}</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight nnc-text leading-[1.05]">
                {service.title}
              </h1>
              <p className="mt-6 text-lg leading-relaxed nnc-text max-w-3xl">
                {service.summary}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="bg-white" data-testid="service-content">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Main column */}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-4">
              <BookOpen className="h-5 w-5 nnc-gold-text" strokeWidth={2} />
              <span className="nnc-overline">Overview</span>
            </div>
            <p className="text-base lg:text-lg leading-relaxed nnc-text">
              {service.overview}
            </p>

            {/* SME Areas accordion (only present on SME service) */}
            {service.smeAreas && service.smeAreas.length > 0 && (
              <div className="mt-14" data-testid="service-sme-areas">
                <div className="flex items-center gap-3 mb-4">
                  <span className="nnc-gold-rule" />
                  <span className="nnc-overline">Areas of Expertise</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold nnc-text tracking-tight">
                  Subject Matter Expertise — by domain.
                </h2>
                <p className="mt-3 text-sm nnc-text max-w-3xl">
                  Tap any domain below to view the scope of expertise we
                  provide. Engagements are scoped in concentrated blocks and
                  matched to a senior practitioner.
                </p>
                <div className="mt-8 border border-[#00205B]">
                  <Accordion type="multiple" className="w-full">
                    {service.smeAreas.map((area) => (
                      <AccordionItem
                        key={area.key}
                        value={area.key}
                        className="border-b border-[#E5E7EB] last:border-b-0"
                        data-testid={`sme-item-${area.key}`}
                      >
                        <AccordionTrigger
                          className="px-6 py-5 text-left hover:bg-[#FBF2DE] hover:no-underline"
                          data-testid={`sme-trigger-${area.key}`}
                        >
                          <span className="flex items-center gap-4 flex-1">
                            <LayoutGrid className="h-4 w-4 nnc-gold-text shrink-0" strokeWidth={2.5} />
                            <span className="font-bold nnc-text text-base lg:text-lg">
                              {area.label}
                            </span>
                          </span>
                        </AccordionTrigger>
                        <AccordionContent
                          className="px-6 pb-6 nnc-ceramic-soft-bg border-t border-[#E5E7EB]"
                          data-testid={`sme-content-${area.key}`}
                        >
                          <p className="text-sm leading-relaxed nnc-text pt-5">
                            {area.body}
                          </p>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </div>
            )}

            {/* Sub-areas */}
            {service.subAreas && service.subAreas.length > 0 && (
              <div className="mt-14" data-testid="service-subareas">
                <div className="flex items-center gap-3 mb-4">
                  <span className="nnc-gold-rule" />
                  <span className="nnc-overline">Sub-Practice Areas</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold nnc-text tracking-tight">
                  Specialized tracks within this practice.
                </h2>
                <div className="mt-8 border border-[#00205B]">
                  {service.subAreas.map((sa, i) => (
                    <div
                      key={i}
                      className={`p-6 lg:p-8 ${i !== 0 ? "border-t border-[#E5E7EB]" : ""}`}
                      data-testid={`service-subarea-${i}`}
                    >
                      <div className="flex items-start gap-4">
                        <span className="inline-block w-8 h-[2px] nnc-gold-bg mt-3" />
                        <div>
                          <h3 className="font-bold text-lg nnc-text">{sa.label}</h3>
                          <p className="mt-2 text-sm leading-relaxed nnc-text">{sa.detail}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Deliverables */}
            {service.deliverables && service.deliverables.length > 0 && (
              <div className="mt-14" data-testid="service-deliverables">
                <div className="flex items-center gap-3 mb-4">
                  <span className="nnc-gold-rule" />
                  <span className="nnc-overline">Representative Deliverables</span>
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold nnc-text tracking-tight">
                  What you receive.
                </h2>
                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.deliverables.map((d, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 border border-[#E5E7EB] p-4 hover:border-[#00205B] transition-colors"
                      data-testid={`service-deliverable-${i}`}
                    >
                      <CheckCircle2 className="h-5 w-5 nnc-gold-text shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span className="text-sm leading-relaxed nnc-text">{d}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Content placeholder note */}
            <div className="mt-14 nnc-ceramic-soft-bg border-l-4 nnc-gold-border p-6" data-testid="service-content-note">
              <div className="nnc-overline mb-2">Editorial Note</div>
              <p className="text-sm leading-relaxed nnc-text">
                Additional case studies, sample work products, and credentials
                for this practice area will be added as content is finalized.
                This page is structured to expand into a complete practice
                profile.
              </p>
            </div>

            {/* "Not sure what you need?" CTA */}
            <Link
              to="/contact"
              className="group mt-12 block border-2 border-[#00205B] transition-colors"
              style={{ backgroundColor: "#FFF4CC" }}
              data-testid="service-not-sure-cta"
            >
              <div className="flex items-center gap-8 p-10 lg:p-12">
                <div className="flex-1">
                  <p className="text-xl lg:text-2xl leading-relaxed nnc-text">
                    <span className="font-bold">
                      Aren&apos;t sure what you need, or need some/all of this?
                    </span>{" "}
                    No worries… we understand.{" "}
                    <span className="font-bold underline">Contact us.</span>
                  </p>
                </div>
                <div className="shrink-0 w-20 h-20 lg:w-24 lg:h-24 nnc-gold-bg flex items-center justify-center border-2 border-[#00205B] group-hover:translate-x-1 transition-transform">
                  <ArrowRight className="h-10 w-10 lg:h-12 lg:w-12 nnc-text" strokeWidth={3} />
                </div>
              </div>
            </Link>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-8" data-testid="service-sidebar">
            {service.audience && service.audience.length > 0 && (
              <div className="border border-[#00205B] p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Users className="h-4 w-4 nnc-gold-text" />
                  <span className="nnc-overline">Who This Serves</span>
                </div>
                <ul className="space-y-2">
                  {service.audience.map((a, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm nnc-text"
                      data-testid={`service-audience-${i}`}
                    >
                      <span className="mt-1.5 inline-block w-2 h-2 nnc-gold-bg shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="nnc-bg text-white p-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] nnc-gold-text mb-3">
                Discuss This Practice
              </div>
              <p className="text-sm text-white/90 leading-relaxed">
                Engagement scoping and project intake will be available through
                our contact channel.
              </p>
              <Link
                to="/contact"
                className="mt-5 inline-flex items-center gap-2 bg-[#FCB814] nnc-text font-bold uppercase tracking-[0.06em] text-xs px-5 py-3 hover:bg-[#fdc73e] transition-colors"
                data-testid="service-sidebar-cta"
              >
                Contact NNC <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="border border-[#E5E7EB] p-6">
              <div className="nnc-overline mb-3">All Practice Areas</div>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 text-sm font-bold nnc-text hover:nnc-gold-text"
                data-testid="service-sidebar-all"
              >
                View the Full Directory <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* PREV / NEXT */}
      <section className="bg-white border-t border-[#E5E7EB]" data-testid="service-prev-next">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {prev && (
            <Link
              to={`/services/${prev.slug}`}
              className="group border border-[#E5E7EB] p-6 hover:border-[#00205B] transition-colors"
              data-testid="service-prev"
            >
              <div className="flex items-center gap-2 nnc-gold-text text-xs font-bold uppercase tracking-[0.2em]">
                <ArrowLeft className="h-3 w-3" /> Previous
              </div>
              <div className="mt-2 text-lg font-bold nnc-text">{prev.title}</div>
              <p className="mt-1 text-sm nnc-text/80" style={{ color: "#00205B" }}>{prev.summary.slice(0, 110)}…</p>
            </Link>
          )}
          {next && (
            <Link
              to={`/services/${next.slug}`}
              className="group border border-[#E5E7EB] p-6 hover:border-[#00205B] transition-colors text-right md:text-right"
              data-testid="service-next"
            >
              <div className="flex items-center justify-end gap-2 nnc-gold-text text-xs font-bold uppercase tracking-[0.2em]">
                Next <ArrowRight className="h-3 w-3" />
              </div>
              <div className="mt-2 text-lg font-bold nnc-text">{next.title}</div>
              <p className="mt-1 text-sm nnc-text/80" style={{ color: "#00205B" }}>{next.summary.slice(0, 110)}…</p>
            </Link>
          )}
        </div>
      </section>
    </main>
  );
};

export default ServiceDetail;
